import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Share2, MessageSquare, Mail, Copy, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { t } from '../lib/i18n';
import { buildShareMessage } from '../lib/shareMessage';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const ShareListButton = ({ shareUrl, lang = 'en', label, onShare, className = '', testid = 'share-list-btn' }) => {
  const [message, setMessage] = useState(shareUrl);
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  useEffect(() => {
    let active = true;
    axios.get(`${API_URL}/public/share-message`)
      .then((res) => {
        if (!active) return;
        const templates = res.data?.templates || {};
        setMessage(buildShareMessage(templates[lang] || templates.en, shareUrl, res.data?.signature));
      })
      .catch(() => { if (active) setMessage(shareUrl); });
    return () => { active = false; };
  }, [lang, shareUrl]);

  const fire = useCallback((action) => {
    if (onShare) onShare(action);
  }, [onShare]);

  const openNative = async () => {
    try {
      await navigator.share({ text: message });
      fire('native');
    } catch {
      // user dismissed the share sheet
    }
  };

  const openUrl = (url, action) => {
    window.open(url, '_blank', 'noopener');
    fire(action);
  };

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success(t('shareCopied', lang));
      fire('copy');
    } catch {
      toast.error('Copy failed — please select the text manually');
    }
  };

  const encoded = encodeURIComponent(message);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button data-testid={testid} className={className || 'rounded-full bg-[#5C4023] hover:bg-[#4A331C] text-white'}>
          <Share2 className="w-4 h-4 mr-1.5" />{label || t('shareThisList', lang)}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-xl bg-white border-[#E0D6C8] w-60">
        <DropdownMenuLabel className="text-xs text-[#888888] font-normal">{t('shareVia', lang)}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {canNativeShare && (
          <DropdownMenuItem data-testid="share-option-native" onClick={openNative} className="cursor-pointer text-sm">
            <Smartphone className="w-4 h-4 mr-2 text-[#C87D3E]" />{t('shareNative', lang)}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem data-testid="share-option-sms" onClick={() => openUrl(`sms:?&body=${encoded}`, 'sms')} className="cursor-pointer text-sm">
          <MessageSquare className="w-4 h-4 mr-2 text-[#C87D3E]" />{t('shareSms', lang)}
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="share-option-whatsapp" onClick={() => openUrl(`https://wa.me/?text=${encoded}`, 'whatsapp')} className="cursor-pointer text-sm">
          <MessageSquare className="w-4 h-4 mr-2 text-[#C87D3E]" />WhatsApp
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="share-option-email" onClick={() => openUrl(`mailto:?subject=${encodeURIComponent(t('shareSubject', lang))}&body=${encoded}`, 'email')} className="cursor-pointer text-sm">
          <Mail className="w-4 h-4 mr-2 text-[#C87D3E]" />{t('shareEmail', lang)}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="share-option-copy" onClick={copyMessage} className="cursor-pointer text-sm">
          <Copy className="w-4 h-4 mr-2 text-[#C87D3E]" />{t('shareCopy', lang)}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
